import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PaginationResponseDto } from '@tiendanube/common';
import { InterfaceIdLabel } from '@tiendanube/components';
import { useAppDispatch } from 'App/store';
import { useListFilters } from 'commons/hooks';
import {
  CustomerFiltersParamsType,
  CustomersFiltersType,
  defaultFilters,
  SortType,
} from 'domains/Customers/Customers/customersServices';
import {
  refreshCustomersList as getCustomersAction,
  getMoreCustomersList as getMoreCustomersAction,
  advancedSearchCustomers as advancedSearchCustomersAction,
  clearCustomerRefreshStatus,
} from 'domains/Customers/Customers/customersSlice';
import {
  getCustomersIds,
  getCustomersPagination,
  getCustomersRefreshStatus,
} from 'domains/Customers/Customers/customersSlice/customersSelectors';
import { useTranslationCustomers } from 'domains/Customers/hooks';
import formatDateFilterChips from 'domains/Metafields/utils';

interface UseCustomerResults {
  isRefreshSuccess: boolean;
  isRefreshError: boolean;
  isRefreshing: boolean;
  customersIds: string[];
  pagination: PaginationResponseDto;
  hasFilters: boolean;
  refreshCustomers: () => Promise<void>;
  getMoreCustomers: () => Promise<void>;
  goToPage: (p: number) => void;
  sortTotalConsumed: (sort: SortType) => void;
  resetCustomerRefreshStatus: () => Promise<void>;
  getCustomers: (customFilters?: any) => void;
  advancedSearchCustomers: (customFilters?: any) => void;
  changeFilters: (customFilters: CustomersFiltersType) => void;
  filters: CustomersFiltersType;
  filterChips: InterfaceIdLabel[];
}

function useCustomerResults(): UseCustomerResults {
  const dispatch = useAppDispatch();
  const t = useTranslationCustomers();

  const { isRefreshing, isRefreshError, isRefreshSuccess } = useSelector(
    getCustomersRefreshStatus,
  );
  const customersIds = useSelector(getCustomersIds);
  const pagination = useSelector(getCustomersPagination);

  const { filters, changeFilters, hasFilters } = useListFilters(
    'customers',
    defaultFilters,
  );

  const filterChips: InterfaceIdLabel[] = useMemo(
    () =>
      Object.entries(filters)
        .filter(
          ([key, value]) => value !== '' && key !== 'page' && key !== 'sort',
        )
        .map(([key, value]) => {
          if (key === 'hasPriceTable') {
            const label = t(
              `customerListPage.filterChips.b2bFilter.${value}`,
              value as string,
            );
            return { id: key, label };
          }

          const label = formatDateFilterChips(value);
          return { id: key, label };
        }),
    [filters, t],
  );

  const getCustomers = useCallback(
    (customFilters?: CustomerFiltersParamsType) => {
      const fetchFilters = customFilters
        ? { ...customFilters, page: 1 }
        : filters;

      changeFilters(fetchFilters);
      dispatch(getCustomersAction(fetchFilters));
    },
    [changeFilters, dispatch, filters],
  );

  const advancedSearchCustomers = useCallback(
    (customFilters?: CustomersFiltersType) => {
      const fetchFilters = customFilters || filters;

      changeFilters(fetchFilters);
      dispatch(advancedSearchCustomersAction(fetchFilters));
    },
    [filters, changeFilters, dispatch],
  );

  const refreshCustomers = useCallback(async () => {
    await dispatch(getCustomersAction({ ...filters, page: 1 }));
  }, [dispatch, filters]);

  const getMoreCustomers = useCallback(async () => {
    await dispatch(getMoreCustomersAction());
  }, [dispatch]);

  const goToPage = useCallback(
    (page: number) => {
      if (filters.page === page) return;
      const newFilters = { ...filters, page };
      changeFilters(newFilters);
      dispatch(getCustomersAction({ ...newFilters, page }));
    },
    [changeFilters, dispatch, filters],
  );

  const sortTotalConsumed = useCallback(
    (sort) => {
      const newFilters = { ...filters, sort };
      changeFilters(newFilters);
      dispatch(getCustomersAction({ ...newFilters }));
    },
    [changeFilters, dispatch, filters],
  );

  const resetCustomerRefreshStatus = useCallback(async () => {
    await dispatch(clearCustomerRefreshStatus());
  }, [dispatch]);

  return {
    isRefreshSuccess,
    isRefreshError,
    isRefreshing,
    customersIds,
    pagination,
    hasFilters,
    filters,
    filterChips,
    refreshCustomers,
    getMoreCustomers,
    goToPage,
    getCustomers,
    advancedSearchCustomers,
    changeFilters,
    sortTotalConsumed,
    resetCustomerRefreshStatus,
  };
}

export default useCustomerResults;
